// Motor do Chat seguro: Signal Protocol (PQXDH + Double Ratchet) via @open-e2ee/signal-protocol-sdk.
// Cada navegador é um dispositivo com identidade própria; o texto só existe decifrado neste aparelho.
import { createSignalProtocolClient } from "@open-e2ee/signal-protocol-sdk";
import { generateCompositeSafetyNumber } from "@open-e2ee/signal-protocol-sdk/safety";
import { indexedDbStore } from "@open-e2ee/signal-protocol-sdk/local/store/web";
import { api } from "@/lib/api";
import { ApiRelay } from "./relay";
import { chatDb } from "./db";
import { encryptFile, decryptFile } from "./crypto";

const DEVICE_KEY = "ga_chat_device";
const silent = { debug() {}, info() {}, warn() {}, error() {} };
const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36));

class ChatEngine extends EventTarget {
  constructor() { super(); this.ready = false; this.me = null; this.myUserId = null; this.deviceUid = null; this._owners = new Map(); this._peerDevices = new Map(); }

  emit(type, detail) { this.dispatchEvent(new CustomEvent(type, { detail })); }

  start(userId) {
    if (this.ready && this.myUserId === userId) return Promise.resolve(this);
    if (!this._starting) this._starting = this._start(userId).finally(() => { this._starting = null; });
    return this._starting;
  }

  async _start(userId) {
    if (this.ready) await this.stop();
    this.myUserId = userId;
    const { data } = await api.get("/chat/me");
    if (!data.profile) { const e = new Error("profile_required"); e.code = "profile_required"; throw e; }
    this.me = data;
    let duid = localStorage.getItem(DEVICE_KEY);
    if (!duid || !data.devices.some((d) => d.device_uid === duid)) {
      const label = navigator.userAgent.includes("Android") ? "Android" : /iPhone|iPad/.test(navigator.userAgent) ? "iPhone/iPad" : "Navegador";
      duid = (await api.post("/chat/devices", { label })).data.device_uid;
      localStorage.setItem(DEVICE_KEY, duid);
      await chatDb.setMeta("device_uid", duid);
    }
    this.deviceUid = duid;
    this.relay = new ApiRelay(duid);
    this.relay.onEvent = (ev) => this.emit("event", ev);
    this.relay.onRemoteIdentity = (duid, identity) => this._pinIdentity(duid, identity);
    this.relay.subscribeRelayConnectionState((s) => this.emit("connection", s));
    const storage = await indexedDbStore();
    this.signal = await createSignalProtocolClient({ identity: { userId: duid }, adapters: { storage, relay: this.relay }, logger: silent });
    this.signal.registerHook("onMessageDecrypted", (env) => this._incoming(env));
    this.signal.registerHook("onDecryptionError", () => this.emit("decrypt_error"));
    this.signal.startRelaySubscription();
    this._purge = setInterval(() => this.purge(), 5000);
    this.ready = true;
    this.emit("ready");
    return this;
  }

  async stop() {
    clearInterval(this._purge);
    try { await this.signal?.stop(); } catch { /* noop */ }
    this.ready = false; this.signal = null; this.relay = null;
  }

  async purge() {
    const ids = await chatDb.purgeExpired();
    if (ids.length) this.emit("expired", ids);
  }

  async ownerOf(deviceUid) {
    if (this._owners.has(deviceUid)) return this._owners.get(deviceUid);
    const { data } = await api.get(`/chat/relay/identity/${deviceUid}`);
    this._owners.set(deviceUid, data.user_id);
    return data.user_id;
  }

  async peerDevices(peerUserId, fresh = false) {
    const c = this._peerDevices.get(peerUserId);
    if (!fresh && c && Date.now() - c.at < 60000) return c.list;
    const { data } = await api.get(`/chat/relay/devices/${peerUserId}`);
    this._peerDevices.set(peerUserId, { list: data, at: Date.now() });
    return data;
  }

  async _fanout(peerUserId, payload, syncPayload) {
    let devices;
    try { devices = await this.peerDevices(peerUserId, true); } catch { throw new Error("Não foi possível enviar. Verifique se o contato ainda está disponível."); }
    if (!devices.length) throw new Error("Este contato ainda não ativou o Chat em nenhum aparelho.");
    const body = JSON.stringify(payload);
    const results = await Promise.allSettled(devices.map((d) => this.signal.send(d, body)));
    const ok = results.filter((r) => r.status === "fulfilled");
    if (!ok.length) {
      const err = results[0].reason;
      throw new Error(err?.response?.status === 403 ? "Não foi possível enviar. Verifique se o contato ainda está disponível." : "Não foi possível entregar a mensagem agora. Tente novamente.");
    }
    const mine = (this.me?.devices || []).filter((d) => d.device_uid !== this.deviceUid);
    Promise.allSettled(mine.map((d) => this.signal.send(d.device_uid, JSON.stringify(syncPayload || payload)))).catch(() => {});
    return ok.map((r) => r.value?.clientMessageId).filter(Boolean);
  }

  async send(peerUserId, { text, ttl = 0, once = false }) {
    const now = Date.now();
    const msg = { t: "msg", id: uuid(), text, sentAt: now, ttl, once };
    const local = { id: msg.id, peer: peerUserId, mine: true, text, sentAt: now, ttl, once, expiresAt: ttl && !once ? now + ttl * 1000 : null, status: "sending", cmids: [] };
    await chatDb.put(local);
    this.emit("message", local);
    try {
      local.cmids = await this._fanout(peerUserId, msg, { t: "sync", peer: peerUserId, msg });
      local.status = "sent";
    } catch (e) {
      local.status = "failed";
      await chatDb.put(local); this.emit("message", local);
      throw e;
    }
    await chatDb.put(local);
    this.emit("message", local);
    return local;
  }

  async sendFile(peerUserId, file, { ttl = 0, once = false, caption = "" } = {}) {
    if (file.size > 25 * 1024 * 1024) throw new Error("O anexo deve ter no máximo 25 MB.");
    const now = Date.now();
    const enc = await encryptFile(file);
    const local = { id: uuid(), peer: peerUserId, mine: true, text: caption, kind: "file", att: { name: enc.name, mime: enc.mime, size: enc.size }, blob: file, sentAt: now, ttl, once, expiresAt: ttl && !once ? now + ttl * 1000 : null, status: "sending", cmids: [] };
    await chatDb.put(local);
    this.emit("message", local);
    try {
      const { data } = await api.post("/chat/attachments", enc.cipher, { headers: { "Content-Type": "application/octet-stream" }, timeout: 180000 });
      const att = { id: data.id, key: enc.key, iv: enc.iv, digest: enc.digest, name: enc.name, mime: enc.mime, size: enc.size };
      const msg = { t: "msg", id: local.id, kind: "file", att, text: caption, sentAt: now, ttl, once };
      local.cmids = await this._fanout(peerUserId, msg, { t: "sync", peer: peerUserId, msg });
      local.status = "sent"; local.att = att;
    } catch (e) {
      local.status = "failed"; await chatDb.put(local); this.emit("message", local);
      throw new Error(e?.response?.data?.detail?.message || e?.response?.data?.detail || e?.message || "Não foi possível enviar o anexo.");
    }
    await chatDb.put(local); this.emit("message", local);
    return local;
  }

  async _fetchAttachment(local) {
    try {
      const { data } = await api.get(`/chat/attachments/${local.att.id}`, { responseType: "arraybuffer", timeout: 180000 });
      local.blob = await decryptFile(data, local.att);
    } catch (e) {
      local.attError = e?.response?.status === 404 ? "Anexo expirado ou removido." : (e?.message || "Não foi possível baixar o anexo.");
    }
    await chatDb.put(local);
    this.emit("message", local);
  }

  async deleteForEveryone(msg) {
    await this._fanout(msg.peer, { t: "del", id: msg.id });
    for (const c of msg.cmids || []) { try { await api.delete(`/chat/relay/envelopes/by-client/${c}`); } catch { /* já entregue */ } }
    if (msg.att?.id) { try { await api.delete(`/chat/attachments/${msg.att.id}`); } catch { /* já removido */ } }
    await chatDb.remove(msg.id);
    this.emit("removed", msg.id);
  }

  async deleteForMe(id) { await chatDb.remove(id); this.emit("removed", id); }

  async consumeOnce(msg) {
    // Visualização única: o conteúdo some deste aparelho e o remetente é avisado.
    await chatDb.remove(msg.id);
    this.emit("removed", msg.id);
    try { await this._fanout(msg.peer, { t: "viewed", id: msg.id }); } catch { /* sem conexão */ }
  }

  async _incoming(env) {
    let data; try { data = JSON.parse(env.content); } catch { return; }
    const owner = await this.ownerOf(env.senderId).catch(() => null);
    if (!owner) return;
    if (data.t === "sync" && owner === this.myUserId && data.msg) {
      const m = data.msg;
      const local = { id: m.id, peer: data.peer, mine: true, text: m.text, sentAt: m.sentAt, ttl: m.ttl, once: m.once, expiresAt: m.ttl && !m.once ? Date.now() + m.ttl * 1000 : null, status: "sent" };
      if (m.kind === "file" && m.att?.id) { local.kind = "file"; local.att = m.att; }
      await chatDb.put(local);
      this.emit("message", { peer: data.peer });
      if (local.kind === "file") this._fetchAttachment(local);
      return;
    }
    if (owner === this.myUserId) return;
    if (data.t === "msg") {
      const now = Date.now();
      const local = { id: data.id, peer: owner, mine: false, text: String(data.text || ""), sentAt: data.sentAt || now, receivedAt: now, ttl: data.ttl || 0, once: !!data.once, expiresAt: data.ttl && !data.once ? now + data.ttl * 1000 : null, read: false };
      if (data.kind === "file" && data.att?.id) { local.kind = "file"; local.att = data.att; }
      await chatDb.put(local);
      this.emit("message", local);
      if (local.kind === "file") this._fetchAttachment(local);
      return;
    }
    if (data.t === "del") { const m = await chatDb.get(data.id); if (m && m.peer === owner) { await chatDb.remove(data.id); this.emit("removed", data.id); } return; }
    if (data.t === "viewed") {
      const m = await chatDb.get(data.id);
      if (m && m.peer === owner && m.mine) {
        if (m.att?.id) { try { await api.delete(`/chat/attachments/${m.att.id}`); } catch { /* já removido */ } }
        await chatDb.put({ ...m, text: "", blob: undefined, att: m.att ? { name: m.att.name, mime: m.att.mime, size: m.att.size } : undefined, viewed: true });
        this.emit("message", m);
      }
    }
  }

  // Confiança no primeiro uso (TOFU): a identidade de cada dispositivo é fixada localmente; mudanças geram aviso.
  async _pinIdentity(duid, identity) {
    if (duid === this.deviceUid || !identity) return;
    const key = `identity:${duid}`;
    const pinned = await chatDb.getMeta(key);
    const fp = JSON.stringify(identity);
    if (!pinned) { await chatDb.setMeta(`identity_at:${duid}`, Date.now()); return chatDb.setMeta(key, fp); }
    if (pinned !== fp) {
      await chatDb.setMeta(`identity_changed:${duid}`, fp);
      this.emit("identity_changed", { device: duid });
    }
  }

  async localIdentity() {
    if (this.relay?.localIdentity) return this.relay.localIdentity;
    const { data } = await api.get(`/chat/relay/identity/${this.deviceUid}`);
    return (this.relay.localIdentity = data.identity);
  }

  async _sideIdentities(deviceUids) {
    const out = [];
    for (const d of deviceUids) {
      const pinned = await chatDb.getMeta(`identity:${d}`);
      const identity = pinned ? JSON.parse(pinned) : await this.relay.getIdentityKey(d).catch(() => null);
      if (identity) out.push({ device: d, identity });
    }
    return out;
  }

  // Número de segurança da conversa: combina as identidades de TODOS os aparelhos dos dois lados (simétrico).
  // Cada lado resume suas chaves públicas (SHA-256) e o SDK gera o fingerprint Signal a partir dos dois resumos.
  async safetyNumber(peerUserId, peerUsername) {
    const mine = await this._sideIdentities(await this.peerDevices(this.myUserId, true));
    const theirs = await this._sideIdentities(await this.peerDevices(peerUserId, true));
    if (!mine.length || !theirs.length) return null;
    const digest = async (items, salt) => {
      const keys = items.map((i) => `${i.identity.x25519PublicKey}|${i.identity.ed25519PublicKey}`).sort().join("\n");
      const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(salt + keys));
      return btoa(String.fromCharCode(...new Uint8Array(buf)));
    };
    const side = async (items) => ({ version: 1, x25519PublicKey: await digest(items, "x:"), ed25519PublicKey: await digest(items, "e:") });
    const sn = generateCompositeSafetyNumber(await side(mine), await side(theirs), this.me.profile.username, peerUsername);
    const fp = sn.numeric;
    const key = `sn:${peerUserId}`;
    const known = await chatDb.getMeta(key);
    if (!known) await chatDb.setMeta(key, fp);
    const changed = !!known && known !== fp;
    return { numeric: sn.numeric, emojis: sn.emojis, fp, verified: (await chatDb.getMeta(`sn_verified:${peerUserId}`)) === fp, changed, mine: mine.length, theirs: theirs.length };
  }

  async setVerified(peerUserId, fp, on) {
    await chatDb.setMeta(`sn_verified:${peerUserId}`, on ? fp : "");
    await chatDb.setMeta(`sn:${peerUserId}`, fp);
    this.emit("verified_changed", { peer: peerUserId });
  }

  async acceptChange(peerUserId, fp) {
    await chatDb.setMeta(`sn:${peerUserId}`, fp);
    await chatDb.setMeta(`sn_verified:${peerUserId}`, "");
    for (const d of await this.peerDevices(peerUserId)) {
      const nf = await chatDb.getMeta(`identity_changed:${d}`);
      if (nf) { await chatDb.setMeta(`identity:${d}`, nf); await chatDb.setMeta(`identity_changed:${d}`, ""); try { await this.signal.acceptIdentityRotation?.(d, JSON.parse(nf)); } catch { /* recria sessão no próximo envio */ } }
    }
    this.emit("verified_changed", { peer: peerUserId });
  }

  async trustState(peerUserId, peerUsername) {
    try {
      const sn = await this.safetyNumber(peerUserId, peerUsername);
      if (!sn) return { verified: false, changed: false };
      let changed = sn.changed;
      for (const d of await this.peerDevices(peerUserId)) if (await chatDb.getMeta(`identity_changed:${d}`)) changed = true;
      return { verified: sn.verified && !changed, changed };
    } catch { return { verified: false, changed: false }; }
  }
}

export const chat = new ChatEngine();
export const ttlOptions = [
  { value: 0, label: "Não expira" }, { value: 30, label: "30 segundos" }, { value: 60, label: "1 minuto" },
  { value: 86400, label: "24 horas" }, { value: 604800, label: "7 dias" },
];
